import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import swal from "sweetalert2";

import { ModalEvidenciaComponent } from "src/app/reusable/modalEvidencia/modalEvidencia.component";
import { MensajesFijosEnum } from "src/app/utilerias/enums/mensajes-fijos-enum";

/**
 * Centraliza el manejo de evidencias (previsualizacion y descarga).
 *
 * Antes cada pantalla repetia esta logica y arrastraba los mismos defectos:
 * la extension se comparaba sin normalizar (una foto "IMG.JPG" nunca abria),
 * los blobs de video se etiquetaban como application/octet-stream (el elemento
 * <video> los rechaza) y los archivos no previsualizables no ofrecian descarga.
 */
@Injectable({ providedIn: "root" })
export class EvidenciaViewerService {
  private static readonly MIME_IMAGENES: { [extension: string]: string } = {
    bmp: "image/bmp",
    gif: "image/gif",
    jpeg: "image/jpeg",
    jpg: "image/jpeg",
    png: "image/png",
    svg: "image/svg+xml",
    webp: "image/webp",
  };

  private static readonly MIME_VIDEOS: { [extension: string]: string } = {
    m4v: "video/mp4",
    mov: "video/mp4",
    mp4: "video/mp4",
    ogv: "video/ogg",
    webm: "video/webm",
  };

  private static readonly MIME_DOCUMENTOS: { [extension: string]: string } = {
    csv: "text/csv",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    pdf: "application/pdf",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    txt: "text/plain",
    xls: "application/vnd.ms-excel",
    xlsm: "application/vnd.ms-excel.sheet.macroEnabled.12",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    zip: "application/zip",
  };

  constructor(private dialog: MatDialog) {}

  /**
   * Extension en minusculas. El backend conserva el case original del archivo
   * subido, asi que comparar sin normalizar dejaba fuera nombres como "FOTO.PNG".
   */
  obtenExtension(nombre: string): string {
    if (!nombre) {
      return "";
    }

    const limpio = String(nombre).split("?")[0].split("#")[0].trim();
    const partes = limpio.split(".");

    return partes.length > 1 ? partes.pop().toLowerCase() : "";
  }

  esImagen(nombre: string): boolean {
    return !!EvidenciaViewerService.MIME_IMAGENES[this.obtenExtension(nombre)];
  }

  esVideo(nombre: string): boolean {
    return !!EvidenciaViewerService.MIME_VIDEOS[this.obtenExtension(nombre)];
  }

  /** Indica si la evidencia se puede abrir en el visor o solo se descarga. */
  esPrevisualizable(nombre: string): boolean {
    return this.esImagen(nombre) || this.esVideo(nombre);
  }

  /** MIME real del archivo; sin el, el navegador rechaza videos e imagenes. */
  obtenMimeType(nombre: string): string {
    const extension = this.obtenExtension(nombre);

    return (
      EvidenciaViewerService.MIME_IMAGENES[extension] ||
      EvidenciaViewerService.MIME_VIDEOS[extension] ||
      EvidenciaViewerService.MIME_DOCUMENTOS[extension] ||
      "application/octet-stream"
    );
  }

  /** Icono de Material que representa el tipo de archivo. */
  obtenIcono(nombre: string): string {
    if (this.esImagen(nombre)) {
      return "image";
    }

    if (this.esVideo(nombre)) {
      return "movie";
    }

    switch (this.obtenExtension(nombre)) {
      case "csv":
      case "xls":
      case "xlsm":
      case "xlsx":
        return "table_chart";
      case "pdf":
        return "picture_as_pdf";
      case "doc":
      case "docx":
        return "description";
      case "7z":
      case "rar":
      case "zip":
        return "folder_zip";
      default:
        return "insert_drive_file";
    }
  }

  /** Abre el visor con una imagen entregada por el backend. */
  abreImagen(nombre: string, datos: ArrayBuffer | Blob): void {
    const blob = this.creaBlob(datos, this.obtenMimeType(nombre));

    if (!blob) {
      this.avisoNoDisponible();
      return;
    }

    this.abreModal(nombre, blob, true);
  }

  /** Abre el visor con un video entregado por el backend. */
  abreVideo(nombre: string, datos: ArrayBuffer | Blob): void {
    const blob = this.creaBlob(datos, this.obtenMimeType(nombre));

    if (!blob) {
      this.avisoNoDisponible();
      return;
    }

    this.abreModal(nombre, blob, false);
  }

  /** Entrega el archivo al usuario como descarga del navegador. */
  descarga(nombre: string, datos: ArrayBuffer | Blob): void {
    const blob = this.creaBlob(datos, this.obtenMimeType(nombre));

    if (!blob) {
      this.avisoNoDisponible();
      return;
    }

    const fileUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = nombre;
    link.rel = "noopener";
    // Firefox exige que el ancla este en el DOM para disparar la descarga.
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revocar de inmediato aborta la descarga en algunos navegadores.
    setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60000);
  }

  /** Aviso estandar cuando el backend no devuelve el archivo. */
  avisoNoDisponible(): void {
    swal.fire({
      text: MensajesFijosEnum.EVIDENCIA_NO_DISPONIBLE,
    });
  }

  private abreModal(nombre: string, blob: Blob, esImagen: boolean): void {
    const fileUrl = window.URL.createObjectURL(blob);

    const dialogRef = this.dialog.open(ModalEvidenciaComponent, {
      panelClass: "custom-modalbox",
      data: {
        imagen: fileUrl,
        NombreEvidencia: nombre,
        mimeType: blob.type,
        show: esImagen,
      },
    });

    dialogRef.afterClosed().subscribe(() => window.URL.revokeObjectURL(fileUrl));
  }

  /**
   * Normaliza la respuesta del backend (ArrayBuffer o Blob) y le asigna el MIME
   * correcto; devuelve null cuando la respuesta viene vacia.
   */
  private creaBlob(datos: ArrayBuffer | Blob, mimeType: string): Blob {
    if (!datos) {
      return null;
    }

    if (datos instanceof Blob) {
      return datos.size ? datos.slice(0, datos.size, mimeType) : null;
    }

    return datos.byteLength ? new Blob([datos], { type: mimeType }) : null;
  }
}
